import styles from '../../popups/MessageDefinitionPopup/tabs/RecipientsTab/index.module.css';
import "./index.css";

import React from 'react';
import DialogComponent, {DIALOG_TYPE} from 'Core/components/DialogComponent';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import * as pageConfig from '../../../ItemPage/config';
import * as actions from '../../popups/MessageDefinitionPopup/tabs/RecipientsTab/actions';
import {getPageActions} from 'Core/helpers/redux';
import {reducerStoreKey} from '../../popups/MessageDefinitionPopup/tabs/RecipientsTab/reducer';
import {reducerStoreKey as campaignReducerStoreKey} from 'Pages/apps/default/projectPages/campaign/reducer';
import {selectors} from 'Core/store/reducers';
import * as filterDataMap from '../../popups/MessageDefinitionPopup/tabs/RecipientsTab/dataMap/customerMessageFilter';
import {getTabTitle} from '../../popups/MessageDefinitionPopup/helper';
import {areAllObjectPropsEmpty, getArray, getString, isset} from 'Core/helpers/data';
import {Tooltip} from 'Core/components/global/Tooltip';
import Icon from 'Core/components/display/Icon';
import {icon_font_warning_symbol} from 'Config/app';
import Spinner from 'Core/components/display/Spinner';
import Label from 'Core/components/display/Label';
import SimpleStaticSearch, {
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import {BUTTON_STYLE} from 'Core/components/display/Button';
import {STANDARD_DATE_TIME_FORMAT} from 'Core/const/datetime';
import {
	CUSTOMER_MESSAGE_STATUS_CLASS_MAP,
	CUSTOMER_MESSAGE_STATUSES
} from 'Pages/apps/default/const';
import CustomerCustomFieldFilter from 'Pages/apps/default/customers/components/CustomerCustomFieldFilter';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import {setMessageDefinitionCustomerMessagesGeneratingAction} from 'Pages/apps/default/projectPages/campaign/actions';
import {fetchCustomerCustomFieldsAction} from 'Components/advanced/ImportCustomersFromList/actions';
import DataTable, {DATA_TABLE_CELL_TYPE} from 'Core/components/advanced/DataTable';
import {
	DataTableCellAnyTypeOptionsDataObject, DataTableCellDateTypeOptionsDataObject,
	DataTableCellTextTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import {getAppLocaleDatetimeFormat} from 'Core/helpers/locale';
import {LOCALE_DATE_FORMAT_NAME, LOCALE_TIME_FORMAT_NAME} from 'Core/const/locale';
import {PAGINATION_TYPE} from 'Core/components/action/Pagination';

/**
 * Get all actions used by this component
 * @type {Object}
 */
const allActions = getPageActions({
	...actions, setMessageDefinitionCustomerMessagesGeneratingAction, fetchCustomerCustomFieldsAction,
});

/**
 * Get state props from Redux store
 * @param {Object} state - Redux store state
 * @param {Object} ownProps - Component's own props
 * @return {Object}
 */
const mapStateToProps = (state, ownProps) => ({
	campaign: selectors[campaignReducerStoreKey].getCampaignItem(state),
	mainList: selectors[reducerStoreKey].getCustomerMessageList(state),
	mainListPagination: selectors[reducerStoreKey].getCustomerMessageListPagination(state),
	mainListSort: selectors[reducerStoreKey].getCustomerMessageListSort(state),
	mainListFilter: selectors[reducerStoreKey].getCustomerMessageListFilter(state),
	isGeneratingForDefinition: selectors[campaignReducerStoreKey].isMessageDefinitionGeneratingCustomerMessages(
		state, getString(ownProps, 'messageDefinition.id')
	),
});

class DynamicMessageDefinitionDialog extends DialogComponent {
	constructor(props) {
		super(props, {
			dialogType: DIALOG_TYPE.MESSAGE,
			alignContent: 'left',
			translationPath: `${pageConfig.translationPath}.MessageDefinitionPopup.RecipientsTab`,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: ['onClose', 'onOk', 'onYes', 'onNo', ...Object.keys(allActions)],

			// Custom component options
			/**
			 * Current interval to check if customer messages are being generated
			 * @note The value entered here is an initial value that will be used when component mounts, but it will be
			 * changed based on the state of the 'isGeneratingForDefinition' flag when
			 * 'checkGeneratedMessagesIntervalSlow' or 'checkGeneratedMessagesIntervalFast' will be used. This should
			 * probably be the same value as 'checkGeneratedMessagesIntervalSlow' option.
			 * @type {number|null|undefined} If falsy, no interval well be started.
			 */
			checkGeneratedMessagesInterval: 60000,
			/**
			 * Interval to check if customer messages are being generated if they are not being generated
			 * @type {number|null|undefined} If falsy, no interval well be started.
			 */
			checkGeneratedMessagesIntervalSlow: 60000,
			/**
			 * Interval to check if customer messages are being generated if while they are being generated
			 * @type {number|null|undefined} If falsy, no interval well be started.
			 */
			checkGeneratedMessagesIntervalFast: 5000,
			/**
			 * Interval identifier for interval to check if customer messages are being generated
			 */
			generatedMessagesInterval: undefined,
		});
		
		// Initialize local state
		this.state = {
			/**
			 * Customer custom fields
			 * @type {
			 * 	{id: number, name: string, code: string, description: string, type: CustomerCustomFiledType}[]
			 * 	|null|undefined
			 * }
			 */
			customerCustomFields: null,
		};

		// Refs
		this.mainListFilterRef = null;
		this.mainListRef = null;

		// Data methods
		this.startCustomerMessagesGeneratingInterval = this.startCustomerMessagesGeneratingInterval.bind(this);
		this.updateCustomerMessagesGenerating = this.updateCustomerMessagesGenerating.bind(this);
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);
	}

	/**
	 * Replacement for default 'componentDidMount' method that will return a promise
	 * @note This method should be used instead of the default 'componentDidMount' when you need to have async calls in
	 * your 'componentDidMount'.
	 * @important Please do not forget to decrease the value of this.mountCount once async calls finish.
	 * @return {Promise<number|void>} Promise that will resolve with the updated mount count that will be set in the
	 * 'componentDidMount' method or undefined for default functionality where 'componentDidMount' will just reset the
	 * mount count to zero.
	 * @throws {AsyncMountError} Promise can reject with the AsyncMountError in which case another
	 * 'asyncComponentDidMount' will be called if mount count is greater than zero.
	 */
	async asyncComponentDidMount() {
		await super.asyncComponentDidMount();

		const {messageDefinition, loadCustomerMessageListAction, fetchCustomerCustomFieldsAction} = this.props;
		
		// Check if customer messages are being generated
		await this.updateCustomerMessagesGenerating(true);
		
		// Load customer custom fields for search
		await this.executeAbortableActionMount(fetchCustomerCustomFieldsAction)
			// Load customer custom field into local state
			.then(ccf => this.setState({customerCustomFields: ccf}));
		
		// Load main list if it is not already loaded
		await this.executeAbortableActionMount(loadCustomerMessageListAction, messageDefinition.id);
	}

	componentDidMount() {
		super.componentDidMount();

		// Set the interval to periodically check if customer messages are being generated
		this.startCustomerMessagesGeneratingInterval();
	}

	componentWillUnmount() {
		super.componentWillUnmount();

		const {clearCustomerMessageListAction} = this.props;

		clearCustomerMessageListAction();
		
		// Clear interval that checks if customer messages are being generated
		clearInterval(this.getOption('generatedMessagesInterval'));
	}
	

	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Start the interval to periodically check if customer messages are being generated
	 */
	startCustomerMessagesGeneratingInterval() {
		const checkGeneratedMessagesInterval = this.getOption('checkGeneratedMessagesInterval');
		const generatedMessagesInterval = this.getOption('generatedMessagesInterval');

		// Clear previous interval (if any)
		if (generatedMessagesInterval) clearInterval(generatedMessagesInterval);

		// Set the new interval
		if (checkGeneratedMessagesInterval) {
			this.setOption(
				'generatedMessagesInterval',
				setInterval(() => this.updateCustomerMessagesGenerating(false, true), checkGeneratedMessagesInterval)
			);
		}
	}

	/**
	 * Update local state flag for customer messages generating
	 *
	 * @param {boolean} [onMount=false] - Flag that specifies if this method is called as part of the mount cycle.
	 * @param {boolean} [ignoreErrors=false] - Flag that specifies if response errors will be ignored.
	 * @return {Promise<Object>}
	 */
	updateCustomerMessagesGenerating(onMount = false, ignoreErrors = false) {
		const {
			messageDefinition, isGeneratingForDefinition, areCustomerMessagesGeneratingAction, addDefaultMessageAction,
			setMessageDefinitionCustomerMessagesGeneratingAction
		} = this.props;
		const executeAbortableAction = (onMount ? this.executeAbortableActionMount : this.executeAbortableAction);

		return executeAbortableAction(areCustomerMessagesGeneratingAction, messageDefinition.id, ignoreErrors)
			.then(newCustomerMessagesGenerating => {
				// Extend the interval for checking if messages are being generated to a minute or more if checking failed
				if (!isset(newCustomerMessagesGenerating)) {
					this.setOption('checkGeneratedMessagesInterval', this.getOption('checkGeneratedMessagesIntervalSlow'));
					this.startCustomerMessagesGeneratingInterval();
				}
					// Shorten the interval for checking if messages are being generated to a few seconds while they are being
				// generated to detect when they are done as soon as possible
				else if (newCustomerMessagesGenerating) {
					this.setOption('checkGeneratedMessagesInterval', this.getOption('checkGeneratedMessagesIntervalFast'));
					this.startCustomerMessagesGeneratingInterval();
				}
					// Extend the interval for checking if messages are being generated to a minute or more while if they are 
				// not being generated to reduce the number of API calls
				else {
					this.setOption('checkGeneratedMessagesInterval', this.getOption('checkGeneratedMessagesIntervalSlow'));
					this.startCustomerMessagesGeneratingInterval();
				}

				// Handle done generating
				setMessageDefinitionCustomerMessagesGeneratingAction(messageDefinition.id, newCustomerMessagesGenerating);
				// Reload main list with current options but without the loading overlay because this should be a 
				// background process
				if (
					isset(newCustomerMessagesGenerating) && !newCustomerMessagesGenerating &&
					isGeneratingForDefinition
				) {
					this.reloadMainList(false).then();
					// Add a done message to notify the user
					addDefaultMessageAction(this.tt('messages_generated_done', 'recipients_section'));
				}
			})
			.catch(e => {
				setMessageDefinitionCustomerMessagesGeneratingAction(messageDefinition.id, undefined);
				throw e;
			});
	}

	/**
	 * Reload main list using current options (page, sort, ...)
	 *
	 * @param {boolean} [loadingOverlay=true] - Flag that specifies if loading overlay should be visible.
	 * @return {Promise<*>}
	 */
	reloadMainList(loadingOverlay = true) {
		const {
			messageDefinition, loadCustomerMessageListAction, mainListPagination, mainListSort, mainListFilter
		} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerMessageListAction, messageDefinition.id, oFilter, pageNo, perPage, sortBy, sortDir, loadingOverlay
		)
			.then(res => {
				this.mainListFilterRef?.reload();
				return res;
			});
	}

	/**
	 * Reload main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {
			messageDefinition, loadCustomerMessageListAction, mainListPagination, mainListSort, mainListFilter
		} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerMessageListAction, messageDefinition.id, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {messageDefinition, loadCustomerMessageListAction, mainListPagination, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const oFilter = filterDataMap.output(mainListFilter);

		return this.executeAbortableAction(
			loadCustomerMessageListAction, messageDefinition.id, oFilter, pageNo, perPage, sortBy, sortDir
		);
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {messageDefinition, loadCustomerMessageListAction, mainListPagination, mainListSort} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = filterDataMap.output(filter);

		return this.executeAbortableAction(
			loadCustomerMessageListAction, messageDefinition.id, oFilter, 1, perPage, sortBy, sortDir
		)
			.then(() => {
				if (areAllObjectPropsEmpty(oFilter, true, false)) {
					if (this.mainListFilterRef) this.mainListFilterRef.close();
				}
			});
	}

	/**
	 * Remove main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}
	
	render() {
		const {
			campaign, messageDefinition, isGeneratingForDefinition, mainList, mainListPagination, mainListSort, 
			mainListFilter
		} = this.props;
		const {customerCustomFields} = this.state;
		
		return this.renderDialog(
			this.renderTitle(
				(
					<div className="campaign-item-dialog-title">
						<Label element="small" content={getTabTitle(campaign)} />
						<Icon className="title-icon" symbol="bell-o" />
						<div className="title-content">
							<Label
								element="div"
								content={(
									isset(messageDefinition.ordinal) && messageDefinition.ordinal !== null ?
										`${this.t(
											'Ping ID', 
											'AppsSection.DefaultApp.projectPages.CampaignPage.ItemPage.messageDefinition'
										)}${messageDefinition.ordinal}` : ''
								)}
							/>
						</div>
					</div>
				)
			),
			(
				<div className={styles['wrapper']}>
					<div className={`customer-messages-section ${styles['customerMessages']}`}>
						<div className={`separator ${styles['sectionTitle']}`}>
							{
								!isset(isGeneratingForDefinition) ?
									<Tooltip
										title={this.tt('generating_error_tooltip', 'customer_messages_section')}
										size="small"
										position="top-center"
										arrow={true}
										interactive={false}
									>
										<Icon symbol="bell" className={styles['icon']}/>
										<Icon className={`${styles['iconDot']}`} symbol={icon_font_warning_symbol}/>
									</Tooltip>
								: isGeneratingForDefinition ?
									<Tooltip
										title={this.tt('generating_tooltip', 'customer_messages_section')}
										size="small"
										position="top-center"
										arrow={true}
										interactive={false}
									>
										<Icon symbol="bell" className={styles['icon']}/>
										<span className={`${styles['iconDot']}`}>
											<Spinner
												color="var(--action-color-contrast-main)"
												size={8}
												weight={4}
												linear={true}
												slow={true}
											/>
										</span>
									</Tooltip>
								:
									<Icon symbol="bell" className={styles['icon']}/>
							}
							<Label content={this.tt('title', 'customer_messages_section')}/>
						</div>
	
						<SimpleStaticSearch
							styleName="compact"
							className={`main-search rows-4 ${styles['filter']}`}
							defaultCollapse={true}
							layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
							buttonProps={{
								displayStyle: BUTTON_STYLE.DEFAULT
							}}
							options={[
								new SimpleStaticSearchOptionObject(
									'scheduledDateFrom',
									this.t('scheduledDateFromField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
									{
										valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
									}
								),
								new SimpleStaticSearchOptionObject(
									'scheduledDateTo',
									this.t('scheduledDateToField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
									{
										valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
									}
								),
								new SimpleStaticSearchOptionObject(
									'deliveryDateFrom',
									this.t('deliveryDateFromField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
									{
										valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
									}
								),
								new SimpleStaticSearchOptionObject(
									'deliveryDateTo',
									this.t('deliveryDateToField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATE,
									{
										valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S
									}
								),
								new SimpleStaticSearchOptionObject(
									'status',
									this.t('statusField'),
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.SELECT,
									{
										isClearable: true,
										options: CUSTOMER_MESSAGE_STATUSES.map(s => ({
											label: this.t(s, 'constants.customerMessageStatus'),
											value: s,
										})),
									}
								),
								new SimpleStaticSearchOptionObject(
									'customerFirstName',
									this.t('customerFirstNameField')
								),
								new SimpleStaticSearchOptionObject(
									'customerLastName',
									this.t('customerLastNameField')
								),
								new SimpleStaticSearchOptionObject(
									'customerCustomFields',
									'',
									SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
									{options: getArray(customerCustomFields).map(i => ({label: i.name, value: i.name}))},
									CustomerCustomFieldFilter,
									{
										labelPosition: FORM_FIELD_LABEL_POSITION.NONE,
										inputClassName: 'customer-custom-field-item custom-field-item'
									}
								),
							]}
							value={mainListFilter}
							title={(<Label icon="search" content={this.t('Search', 'general')}/>)}
							applied={!this.isMainListFilterEmpty()}
							enableToolbar={true}
							enableResetButton={false}
							onChange={this.filterMainList}
							onRemove={this.removeMainListFilter}
							ref={node => {
								this.mainListFilterRef = node;
							}}
						/>
	
						<DataTable
							id="main-page-table"
							responsive={true}
							highlightOnHover={true}
							columns={[
								{
									name: 'status',
									sortName: 'status',
									label: this.t('statusField'),
									dataType: DATA_TABLE_CELL_TYPE.ANY,
									dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
										content: row => (
											<Label
												element="span"
												elementProps={{
													className: `no-wrap tag ${CUSTOMER_MESSAGE_STATUS_CLASS_MAP[row.status]}`
												}}
												content={this.t(row.status, 'constants.customerMessageStatus')}
											/>
										),
									}),
									width: 1,
								},
								{
									name: 'customer.firstName',
									label: this.t('customerFirstNameField'),
								},
								{
									name: 'customer.lastName',
									label: this.t('customerLastNameField'),
								},
								{
									name: 'customer.mobilePhoneNumber',
									label: this.t('mobilePhoneNumberField'),
									dataTypeOptions: new DataTableCellTextTypeOptionsDataObject({
										whiteSpace: 'nowrap',
									}),
									width: 1,
								},
								{
									name: 'scheduledDate',
									sortName: 'scheduledDate',
									label: this.t('scheduledDateField'),
									dataType: DATA_TABLE_CELL_TYPE.DATE,
									dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
										outputFormat: getAppLocaleDatetimeFormat(
											LOCALE_DATE_FORMAT_NAME.SHORT,
											LOCALE_TIME_FORMAT_NAME.STANDARD
										),
										whiteSpace: 'nowrap',
									}),
									defaultValue: '—',
									width: 1,
								},
								{
									name: 'deliveryDate',
									sortName: 'deliveryDate',
									label: this.t('deliveryDateField'),
									dataType: DATA_TABLE_CELL_TYPE.DATE,
									dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
										outputFormat: getAppLocaleDatetimeFormat(
											LOCALE_DATE_FORMAT_NAME.SHORT,
											LOCALE_TIME_FORMAT_NAME.STANDARD
										),
										whiteSpace: 'nowrap',
									}),
									defaultValue: '—',
									width: 1,
								},
							]}
							data={mainList}
							paginationType={PAGINATION_TYPE.DYNAMIC}
							onSortByColumn={this.sortMainList}
							onPaginationClick={this.loadMainListPage}
							onSelect={this.handleMainListSelectionChange}
							{...mainListPagination}
							{...mainListSort}
							ref={node => {
								this.mainListRef = node;
							}}
						/>
					</div>
				</div>
			)
		);
	}
}

/**
 * Define the component's own props that can be passed to it by parent components
 */
DynamicMessageDefinitionDialog.propTypes = {
	messageDefinition: PropTypes.object.isRequired
};

export default connect(mapStateToProps, allActions)(DynamicMessageDefinitionDialog);